import { useEffect, useState } from 'react';
import axios from 'axios';
import {
  BookOpen,
  Save,
  Trash2,
  HelpCircle,
  GitCompare,
  Loader2,
  Check,
  AlertCircle,
  Play,
  FileSearch,
  X,
} from 'lucide-react';
import { getApiBase } from '../apiBase';
import { reviewOracleSql } from './sqlStudioApi';
import { downloadCsv } from './downloadCsv';

const API = axios.create({ baseURL: getApiBase(), withCredentials: true, timeout: 120000 });

const EMPTY_PARAMS = { dateStart: '', dateEnd: '', personNumber: '', legislation: 'US' };

/** Legacy ids that emit abs_audit / 16-col audit SQL — use hcm-us-absence-entries in the composer instead. */
const HIDDEN_RECIPE_IDS = new Set([
  'open-absence-entries',
  'absence-entry-audit',
  'hcm-hdl-load-errors-summary',
]);

/** Drop null/invalid entries so .map never reads `.id` on null (partial API payloads). */
function listWithId(items) {
  return (Array.isArray(items) ? items : []).filter((item) => item != null && item.id != null);
}

function columnNamesFromResult(cols, rows) {
  if (Array.isArray(cols) && cols.length) {
    return cols.map((c) => (typeof c === 'string' ? c : c?.name)).filter(Boolean);
  }
  return Object.keys(rows[0] || {});
}

function compareRuns(a, b) {
  if (!a || !b) return null;
  const colsA = columnNamesFromResult(a.columns, a.rows);
  const colsB = columnNamesFromResult(b.columns, b.rows);
  const setA = new Set(colsA.map((c) => c.toLowerCase()));
  const setB = new Set(colsB.map((c) => c.toLowerCase()));
  const onlyA = colsA.filter((c) => !setB.has(c.toLowerCase()));
  const onlyB = colsB.filter((c) => !setA.has(c.toLowerCase()));
  const shared = colsA.filter((c) => setB.has(c.toLowerCase()));
  return {
    rowCountA: a.rowCount ?? a.rows?.length ?? 0,
    rowCountB: b.rowCount ?? b.rows?.length ?? 0,
    colCountA: colsA.length,
    colCountB: colsB.length,
    onlyA,
    onlyB,
    sharedCount: shared.length,
  };
}

export default function SqlStudioToolbar({
  modeId,
  editorSql,
  setEditorSql,
  onRecipeLoaded,
  onOpenUserLookup,
  runResult,
  setRunResult,
  onValidateBeforeRun,
  executing,
}) {
  const [recipes, setRecipes] = useState([]);
  const [recipeId, setRecipeId] = useState('');
  const [loadingRecipe, setLoadingRecipe] = useState(false);
  const [recipeLoadError, setRecipeLoadError] = useState('');
  const [params, setParams] = useState(EMPTY_PARAMS);
  const [explain, setExplain] = useState(null);
  const [explainBusy, setExplainBusy] = useState(false);
  const [saved, setSaved] = useState([]);
  const [saveName, setSaveName] = useState('');
  const [validation, setValidation] = useState(null);
  const [validating, setValidating] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewMarkdown, setReviewMarkdown] = useState('');
  const [reviewBusy, setReviewBusy] = useState(false);
  const [reviewError, setReviewError] = useState('');
  const [prevRun, setPrevRun] = useState(null);
  const [showCompare, setShowCompare] = useState(false);
  const [paramStatus, setParamStatus] = useState(null);
  const [applyingParams, setApplyingParams] = useState(false);

  useEffect(() => {
    if (modeId !== 'oracle') return;
    API.get('/api/copilot/sql-library', { validateStatus: () => true }).then((res) => {
      if (res.status === 200) {
        setRecipes(listWithId(res.data?.recipes));
        return;
      }
      if (res.status === 401) {
        setRecipeLoadError('Not signed in — log in to load SQL library recipes.');
      }
    });
    API.get('/api/copilot/saved-queries', { validateStatus: () => true }).then((res) => {
      if (res.status === 200) setSaved(listWithId(res.data?.queries));
    });
  }, [modeId]);

  const diff =
    prevRun?.ok && runResult?.ok ? compareRuns(prevRun, runResult) : null;

  async function loadRecipe() {
    if (!recipeId) return;
    setLoadingRecipe(true);
    setRecipeLoadError('');
    try {
      const res = await API.get(`/api/copilot/sql-library/${recipeId}`, { validateStatus: () => true });
      const sql = typeof res.data?.sql === 'string' ? res.data.sql.trim() : '';
      if (res.status === 200 && sql) {
        setEditorSql(sql);
        onRecipeLoaded?.();
        return;
      }
      const msg =
        res.data?.error ||
        (res.status === 401
          ? 'Not signed in — log in to the tool, then try Load recipe again.'
          : res.status === 403
            ? 'AI Copilot is not enabled for your account.'
            : res.status === 404
              ? `Recipe not found: ${recipeId}`
              : res.status === 200 && !sql
                ? `Recipe "${recipeId}" returned no SQL.`
                : `Could not load recipe (HTTP ${res.status}).`);
      setRecipeLoadError(msg);
    } catch (err) {
      setRecipeLoadError(err?.message || 'Could not load recipe.');
    } finally {
      setLoadingRecipe(false);
    }
  }

  async function applyParameters() {
    setApplyingParams(true);
    setParamStatus(null);
    try {
      const res = await API.post(
        '/api/copilot/apply-sql-parameters',
        { sql: editorSql, parameters: params },
        { validateStatus: () => true }
      );
      if (res.status === 200 && res.data.sql) {
        const appliedList = Array.isArray(res.data.appliedParams) ? res.data.appliedParams : [];
        const skippedList = Array.isArray(res.data.skippedParams) ? res.data.skippedParams : [];
        if (res.data.applied) {
          setEditorSql(res.data.sql);
          setParamStatus({
            ok: true,
            message:
              `Filtered by ${appliedList.join(', ') || 'parameters'}.` +
              (skippedList.length
                ? ` No matching column for ${skippedList.join(', ')} — those return all rows.`
                : ''),
          });
        } else {
          setParamStatus({
            ok: false,
            message:
              skippedList.length
                ? `No matching column in this query for ${skippedList.join(', ')} — all rows are returned. Add :PERSON_NUMBER / :DATE_START / :DATE_END / :LEGISLATION_CODE placeholders to filter explicitly.`
                : 'Enter a date range, person number, or legislation above, then apply.',
          });
        }
        return;
      }
      setParamStatus({
        ok: false,
        message:
          res.data?.error ||
          (res.status === 401
            ? 'Not signed in — log in and try again.'
            : `Could not apply parameters (HTTP ${res.status}).`),
      });
    } catch (err) {
      setParamStatus({ ok: false, message: err?.message || 'Could not apply parameters.' });
    } finally {
      setApplyingParams(false);
    }
  }

  async function explainSql() {
    setExplainBusy(true);
    setExplain(null);
    try {
      const res = await API.post('/api/copilot/explain-sql', { sql: editorSql }, { validateStatus: () => true });
      if (res.status === 200) setExplain(res.data);
    } finally {
      setExplainBusy(false);
    }
  }

  async function reviewSql() {
    setReviewBusy(true);
    setReviewError('');
    setReviewMarkdown('');
    try {
      const data = await reviewOracleSql(editorSql, { source: recipeId || 'sql-studio' });
      setReviewMarkdown(data.markdown || '');
      setReviewOpen(true);
    } catch (err) {
      setReviewError(err?.message || 'Could not review SQL.');
      setReviewOpen(false);
    } finally {
      setReviewBusy(false);
    }
  }

  async function validateSql() {
    setValidating(true);
    setValidation(null);
    try {
      const res = await API.post(
        '/api/copilot/validate-oracle-sql',
        {
          sql: editorSql,
          parameters: params,
          joinRecipeId: recipeId || undefined,
          allowFullLibraryRecipe:
            recipeId === 'active-employee-roster' ||
            recipeId === 'pending-worker-report' ||
            recipeId === 'pending-worker-report-bip' ||
            recipeId === 'pending-bpm-approvals' ||
            recipeId === 'hcm-hdl-load-errors-detail' ||
            recipeId === 'hcm-hdl-dat-line-errors' ||
            recipeId === 'hcm-hdl-dat-line-errors-summary',
          allowFullHdlRecipe:
            recipeId === 'active-employee-roster' ||
            recipeId === 'pending-worker-report' ||
            recipeId === 'pending-worker-report-bip' ||
            recipeId === 'pending-bpm-approvals' ||
            recipeId === 'hcm-hdl-load-errors-detail' ||
            recipeId === 'hcm-hdl-dat-line-errors' ||
            recipeId === 'hcm-hdl-dat-line-errors-summary',
        },
        { validateStatus: () => true }
      );
      if (res.status === 200) setValidation(res.data);
      if (onValidateBeforeRun) onValidateBeforeRun(res.data);
    } finally {
      setValidating(false);
    }
  }

  async function saveQuery() {
    const name = saveName.trim() || `Query ${new Date().toLocaleString()}`;
    const res = await API.post(
      '/api/copilot/saved-queries',
      { name, sql: editorSql, mode: modeId },
      { validateStatus: () => true }
    );
    if (res.status === 201) {
      setSaveName('');
      const list = await API.get('/api/copilot/saved-queries', { validateStatus: () => true });
      if (list.status === 200) setSaved(listWithId(list.data?.queries));
    }
  }

  async function deleteSaved(id) {
    await API.delete(`/api/copilot/saved-queries/${id}`, { validateStatus: () => true });
    setSaved((s) => s.filter((q) => q?.id !== id));
  }

  function captureRunForCompare() {
    if (runResult?.ok) setPrevRun({ ...runResult, rows: [...(runResult.rows || [])] });
  }

  if (modeId !== 'oracle') return null;

  return (
    <div className="mb-4 space-y-3 rounded-xl border border-violet-100 bg-violet-50/40 p-4">
      <div className="text-xs font-semibold uppercase tracking-wide text-violet-800">SQL Studio tools</div>

      <div className="flex flex-wrap items-end gap-2">
        <label className="text-[11px] text-slate-600">
          Recipe
          <select
            value={recipeId}
            onChange={(e) => {
              setRecipeId(e.target.value);
              setRecipeLoadError('');
            }}
            className="mt-0.5 block min-w-[12rem] rounded border border-slate-300 bg-white px-2 py-1 text-xs"
          >
            <option value="">— pick library recipe —</option>
            {recipes
              .filter((r) => r?.id && !HIDDEN_RECIPE_IDS.has(r.id))
              .map((r) => (
                <option key={r.id} value={r.id}>
                  {r.id === 'hcm-us-absence-entries'
                    ? 'US absence entries (slim — use for absence audit)'
                    : r.id === 'pending-worker-report-bip'
                      ? 'Pending Worker Report (BIP) — slim Sql Studio preview'
                      : r.title || r.id}
                </option>
              ))}
          </select>
        </label>
        <button
          type="button"
          onClick={loadRecipe}
          disabled={!recipeId || loadingRecipe}
          className="inline-flex items-center gap-1 rounded-md border border-violet-300 bg-white px-2 py-1 text-xs font-medium text-violet-800 hover:bg-violet-100 disabled:opacity-50"
        >
          {loadingRecipe ? <Loader2 className="h-3 w-3 animate-spin" /> : <BookOpen className="h-3 w-3" />}
          Load recipe
        </button>
        {recipeLoadError && (
          <span className="flex items-start gap-1 text-[11px] text-rose-700" role="alert">
            <AlertCircle className="mt-0.5 h-3 w-3 shrink-0" />
            {recipeLoadError}
          </span>
        )}
      </div>

      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
        <label className="text-[11px] text-slate-600">
          Date start
          <input
            type="date"
            value={params.dateStart}
            onChange={(e) => setParams((p) => ({ ...p, dateStart: e.target.value }))}
            placeholder="YYYY-MM-DD"
            title="Filter range start (YYYY-MM-DD); composer uses DATE literals in WHERE"
            className="mt-0.5 block w-full rounded border border-slate-300 px-2 py-1 text-xs"
          />
        </label>
        <label className="text-[11px] text-slate-600">
          Date end
          <input
            type="date"
            value={params.dateEnd}
            onChange={(e) => setParams((p) => ({ ...p, dateEnd: e.target.value }))}
            placeholder="YYYY-MM-DD"
            title="Filter range end (YYYY-MM-DD); result dates display as YYYY-MM-DD in SELECT"
            className="mt-0.5 block w-full rounded border border-slate-300 px-2 py-1 text-xs"
          />
        </label>
        <label className="text-[11px] text-slate-600">
          Person number
          <input
            value={params.personNumber}
            onChange={(e) => setParams((p) => ({ ...p, personNumber: e.target.value }))}
            className="mt-0.5 block w-full rounded border border-slate-300 px-2 py-1 text-xs"
          />
        </label>
        <label className="text-[11px] text-slate-600">
          Legislation
          <input
            value={params.legislation}
            onChange={(e) => setParams((p) => ({ ...p, legislation: e.target.value }))}
            className="mt-0.5 block w-full rounded border border-slate-300 px-2 py-1 text-xs"
          />
        </label>
      </div>
      <div className="space-y-1">
        <button
          type="button"
          onClick={applyParameters}
          disabled={!editorSql.trim() || applyingParams}
          className="inline-flex items-center gap-1 text-xs font-medium text-violet-700 hover:text-violet-900 disabled:opacity-50"
        >
          {applyingParams && <Loader2 className="h-3 w-3 animate-spin" />}
          Apply parameters to SQL
        </button>
        {paramStatus && (
          <p
            className={`flex items-start gap-1 text-[11px] ${
              paramStatus.ok ? 'text-emerald-700' : 'text-amber-700'
            }`}
            role="status"
          >
            {paramStatus.ok ? (
              <Check className="mt-0.5 h-3 w-3 shrink-0" />
            ) : (
              <AlertCircle className="mt-0.5 h-3 w-3 shrink-0" />
            )}
            {paramStatus.message}
          </p>
        )}
      </div>

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={explainSql}
          disabled={!editorSql.trim() || explainBusy}
          className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-2 py-1 text-xs hover:bg-slate-50 disabled:opacity-50"
        >
          {explainBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <HelpCircle className="h-3 w-3" />}
          Explain SQL
        </button>
        <button
          type="button"
          onClick={reviewSql}
          disabled={!editorSql.trim() || reviewBusy}
          className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-2 py-1 text-xs hover:bg-slate-50 disabled:opacity-50"
        >
          {reviewBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <FileSearch className="h-3 w-3" />}
          Review SQL
        </button>
        <button
          type="button"
          onClick={validateSql}
          disabled={!editorSql.trim() || validating}
          className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-2 py-1 text-xs hover:bg-slate-50 disabled:opacity-50"
        >
          {validating ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
          Validate before run
        </button>
        <button
          type="button"
          onClick={captureRunForCompare}
          disabled={!runResult?.ok}
          className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-2 py-1 text-xs hover:bg-slate-50 disabled:opacity-50"
        >
          <GitCompare className="h-3 w-3" />
          Snapshot for compare
        </button>
        <button
          type="button"
          onClick={() => setShowCompare((v) => !v)}
          disabled={!prevRun?.ok || !runResult?.ok}
          className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-2 py-1 text-xs hover:bg-slate-50 disabled:opacity-50"
        >
          Compare runs
        </button>
        {onOpenUserLookup && (
          <button
            type="button"
            onClick={() => onOpenUserLookup(params.personNumber || '')}
            className="text-xs font-medium text-cyan-700 hover:text-cyan-900"
          >
            Open User Lookup →
          </button>
        )}
      </div>

      {reviewError && (
        <p className="flex items-start gap-1 text-[11px] text-rose-700" role="alert">
          <AlertCircle className="mt-0.5 h-3 w-3 shrink-0" />
          {reviewError}
        </p>
      )}

      {reviewOpen && reviewMarkdown && (
        <div className="rounded-md border border-slate-200 bg-white">
          <div className="flex items-center justify-between border-b border-slate-200 px-2 py-1">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-600">SQL review</p>
            <button
              type="button"
              onClick={() => setReviewOpen(false)}
              className="rounded p-0.5 text-slate-500 hover:bg-slate-100 hover:text-slate-800"
              aria-label="Close review"
            >
              <X className="h-3 w-3" />
            </button>
          </div>
          <pre className="max-h-64 overflow-auto whitespace-pre-wrap p-2 font-mono text-[11px] leading-relaxed text-slate-700">
            {reviewMarkdown}
          </pre>
        </div>
      )}

      {explain?.summary && (
        <div className="rounded-md border border-slate-200 bg-white p-2 text-[11px] text-slate-700">
          <strong>Explain:</strong> {explain.summary}
          {explain.domains?.length > 0 && (
            <span className="ml-2 text-slate-500">({explain.domains.join(', ')})</span>
          )}
          {explain.warnings?.length > 0 && (
            <ul className="mt-1 list-disc pl-4 text-amber-800">
              {explain.warnings.slice(0, 5).map((w, i) => (
                <li key={i}>{w.message || w}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {validation && (
        <div
          className={`rounded-md border p-2 text-[11px] ${
            validation.ok ? 'border-emerald-200 bg-emerald-50 text-emerald-900' : 'border-rose-200 bg-rose-50 text-rose-900'
          }`}
        >
          {validation.ok ? (
            <span className="flex items-center gap-1">
              <Check className="h-3 w-3" /> Ready to preview
            </span>
          ) : (
            <span className="flex items-start gap-1">
              <AlertCircle className="h-3 w-3 shrink-0" />
              {(validation.errors || []).join(' · ')}
            </span>
          )}
        </div>
      )}

      {showCompare && diff && (
        <div className="rounded-md border border-indigo-200 bg-white p-2 text-[11px] text-slate-700">
          <div className="font-semibold text-indigo-900">Run comparison</div>
          <p>
            Rows: {diff.rowCountA} → {diff.rowCountB} · Columns: {diff.colCountA} → {diff.colCountB} (shared{' '}
            {diff.sharedCount})
          </p>
          {diff.onlyA.length > 0 && <p>Only in first: {diff.onlyA.join(', ')}</p>}
          {diff.onlyB.length > 0 && <p>Only in second: {diff.onlyB.join(', ')}</p>}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 border-t border-violet-100 pt-2">
        <input
          value={saveName}
          onChange={(e) => setSaveName(e.target.value)}
          placeholder="Saved query name"
          className="rounded border border-slate-300 px-2 py-1 text-xs"
        />
        <button
          type="button"
          onClick={saveQuery}
          disabled={!editorSql.trim()}
          className="inline-flex items-center gap-1 rounded-md bg-violet-700 px-2 py-1 text-xs text-white hover:bg-violet-800 disabled:opacity-50"
        >
          <Save className="h-3 w-3" />
          Save
        </button>
        {saved.length > 0 && (
          <select
            className="rounded border border-slate-300 px-2 py-1 text-xs"
            defaultValue=""
            onChange={(e) => {
              const q = saved.find((s) => s?.id === e.target.value);
              if (q) setEditorSql(q.sql);
              e.target.value = '';
            }}
          >
            <option value="">Load saved…</option>
            {saved.map((q) => (
              <option key={q?.id} value={q?.id}>
                {q?.name ?? q?.id}
              </option>
            ))}
          </select>
        )}
        {runResult?.ok && runResult.rows?.length > 0 && (
          <button
            type="button"
            onClick={() =>
              downloadCsv(
                `sql-studio-${Date.now()}.csv`,
                columnNamesFromResult(runResult.columns, runResult.rows),
                runResult.rows
              )
            }
            className="text-xs text-slate-600 hover:text-slate-900"
          >
            Export results CSV
          </button>
        )}
      </div>
    </div>
  );
}
